/**
 * AcceptanceParticipantsPanel - Situação dos aceites de uma vistoria
 *
 * Lista os participantes exigidos (papel, etapa, situação) e abre a coleta para quem
 * está na etapa atual. O servidor decide quem pode confirmar; aqui é só orientação.
 *
 * @version 1.7.0
 * RELEASE: 2026-10-13 v1.7.0 - "Ver dados do aceite" exibe "Assinado como: Titular/Testemunha"
 *          e a coleta do cliente recebe allow_witness do modelo (escolha Titular/Testemunha).
 * RELEASE: 2026-10-05 v1.6.0 - Aceite do cliente: "Cliente presente" (coleta no aparelho do vistoriador,
 *          origem "Presencial · assistido por") ou "Enviar link ao cliente" (OwnerAcceptanceLinkBar).
 * RELEASE: 2026-10-01 v1.5.0 - Miniaturas da assinatura desenhada e da selfie (URL assinada 5 min) com
 *          ampliação no ImageLightbox, para auditoria antifraude do revisor.
 * RELEASE: 2026-10-01 v1.4.0 - "Ver dados do aceite": dados de quem assinou (nome, CPF mascarado,
 *          nascimento, origem, IP/aparelho, localização, evidências e hashes) vindos de
 *          prepare_checklist_acceptance v1.3.0, para conferência no modal da vistoria.
 * RELEASE: 2026-09-27 v1.3.0 - Coleta usa termo/exigências do participante (formato por momento).
 * RELEASE: 2026-09-27 v1.2.0 - "Coletar aceite" fica desabilitado enquanto houver itens
 *          pendentes (blockedReason), com tooltip explicando o que falta. Servidor
 *          revalida em confirm_checklist_acceptance v1.1.0.
 * RELEASE: 2026-09-27 v1.1.0 - AcceptanceReceiptBar: situação e download do comprovante (Fase 4).
 * RELEASE: 2026-09-27 v1.0.0 - Versão inicial (Aceites Fase 3).
 */
import { Fragment, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronDown, Download, FileCheck2, Loader2, PenLine, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusDotLabel } from '@/components/ui/status-dot-label';
import { AcceptanceDialog } from './AcceptanceDialog';
import { ImageLightbox } from "@/components/tenant/chat/ImageLightbox";
import { createLogger } from "@/lib/logger";
import { OwnerAcceptanceLinkBar } from './OwnerAcceptanceLinkBar';

const log = createLogger("AcceptanceParticipantsPanel");
import {
  PURPOSE_LABEL, ROLE_LABEL, SIGNER_KIND_LABEL, STAGE_LABEL, type AcceptanceParticipant, type AcceptanceSigner, type AcceptanceStage, type PreparedAcceptance,
  downloadAcceptanceReceipt, useChecklistAcceptanceReceipt, useAcceptanceArtifactUrls,
} from '@/hooks/useChecklistAcceptance';

interface Props {
  data: PreparedAcceptance;
  /** Etapas em que a coleta está liberada no status atual da vistoria */
  openStages: AcceptanceStage[];
  defaultName?: string;
  /** Nome do cliente no snapshot da vistoria, distinto do usuário que coleta. */
  ownerName?: string;
  /** Motivo de bloqueio da coleta (ex.: itens pendentes de aprovação); desabilita o botão */
  blockedReason?: string;
  /** v1.5.0: vistoria (para buscar as imagens das evidências) */
  instanceId?: string;
}

export function AcceptanceParticipantsPanel({ data, openStages, defaultName, ownerName, blockedReason, instanceId }: Props) {
  const [active, setActive] = useState<AcceptanceParticipant | null>(null);
  // v1.4.0: participante com os dados do aceite expandidos
  const [expanded, setExpanded] = useState<string | null>(null);
  if (!data.required || !data.participants?.length || !data.request_id) return null;

  return (
    <div className="rounded-lg border bg-card p-3 space-y-2">
      <div className="flex items-center gap-2 text-xs font-semibold">
        <ShieldCheck className="h-4 w-4 text-primary" />
        Aceites exigidos
        {data.purpose && <span className="font-normal text-muted-foreground">· {PURPOSE_LABEL[data.purpose] ?? data.purpose}</span>}
      </div>
      <div className="divide-y">
        {data.participants.map((p) => {
          const accepted = p.status === 'accepted';
          const canCollect = !accepted && openStages.includes(p.required_stage);
          return (
            <div key={p.id} className="py-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <StatusDotLabel tone={accepted ? 'success' : 'warning'} label={accepted ? 'Aceito' : 'Pendente'} />
              <span className="font-medium">{ROLE_LABEL[p.role]}</span>
              <span className="text-muted-foreground">{STAGE_LABEL[p.required_stage]}{!p.is_required && ' (opcional)'}</span>
              {accepted && (
                <span className="text-muted-foreground">
                  {p.signer_name} · {p.accepted_at ? format(new Date(p.accepted_at), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : ''}
                </span>
              )}
              {canCollect && (p.requirements ?? data.requirements) && (p.terms ?? data.terms) && (
                <Button size="sm" variant="outline" className="ml-auto h-7 text-xs"
                  disabled={!!blockedReason} title={blockedReason || undefined}
                  onClick={() => setActive(p)}>
                  {/* v1.6.0: para o cliente, a coleta no aparelho do vistoriador é o aceite presencial */}
                  <PenLine className="mr-1 h-3.5 w-3.5" />{p.role === 'owner' ? 'Cliente presente' : 'Coletar aceite'}
                </Button>
              )}
              {accepted && p.signer && (
                <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs"
                  onClick={() => setExpanded(expanded === p.id ? null : p.id)} aria-expanded={expanded === p.id}>
                  Ver dados do aceite
                  <ChevronDown className={`ml-1 h-3.5 w-3.5 transition-transform ${expanded === p.id ? 'rotate-180' : ''}`} />
                </Button>
              )}
            </div>
            {/* v1.6.0: alternativa ao presencial — link exclusivo de aceite para o cliente */}
            {!accepted && p.role === 'owner' && instanceId && !blockedReason && openStages.includes(p.required_stage) && (
              <OwnerAcceptanceLinkBar instanceId={instanceId} />
            )}
            {accepted && p.signer && expanded === p.id && <SignerDetails signer={p.signer} instanceId={instanceId} participantId={p.id} role={p.role} />}
            </div>
          );
        })}
      </div>
      {/* v1.3.0: cada participante usa o formato (termo + exigências) do seu momento */}
      {active && (active.requirements ?? data.requirements) && (active.terms ?? data.terms) && (
        <AcceptanceDialog open={!!active} onOpenChange={(v) => !v && setActive(null)} requestId={data.request_id}
          participant={active} requirements={(active.requirements ?? data.requirements)!} terms={(active.terms ?? data.terms)!}
          defaultName={active.role === 'owner' ? ownerName : defaultName}
          allowWitness={active.role === 'owner' && !!data.allow_witness} />
      )}
    </div>
  );
}

/** Formata AAAA-MM-DD como DD/MM/AAAA sem conversão de fuso */
const fmtDay = (v: string | null) => (v ? v.slice(0, 10).split('-').reverse().join('/') : '—');

/** v1.4.0: ficha de conferência dos dados registrados no aceite */
function SignerDetails({ signer, instanceId, participantId, role }: { signer: AcceptanceSigner; instanceId?: string; participantId: string; role?: string }) {
  // v1.6.0: aceite do cliente coletado no sistema = presencial assistido pelo usuário
  const internalOrigin = role === 'owner'
    ? `Presencial${signer.actor_name ? ` · assistido por ${signer.actor_name}` : ''}`
    : `Sistema${signer.actor_name ? ` · registrado por ${signer.actor_name}` : ''}`;
  const rows: [string, ReactNode][] = [
    ['Nome', signer.name],
    // v1.7.0: titular/testemunha (somente aceite do cliente; aceites antigos não exibem)
    ...(role === 'owner' && signer.signer_kind
      ? [['Assinado como', SIGNER_KIND_LABEL[signer.signer_kind]] as [string, ReactNode]]
      : []),
    ['CPF', signer.document_masked ?? 'Não informado'],
    ['Data de nascimento', signer.birth_date ? fmtDay(signer.birth_date) : 'Não informada'],
    ['Data e hora', format(new Date(signer.asserted_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })],
    ['Origem', signer.channel === 'acceptance_link' ? 'Link de aceite do cliente' : signer.channel === 'public_link' ? 'Link externo' : internalOrigin],
    ['Endereço IP', signer.ip_address ?? '—'],
    ['Localização', signer.geo
      ? <a className="text-primary underline-offset-2 hover:underline" target="_blank" rel="noopener noreferrer"
          href={`https://www.google.com/maps?q=${Number(signer.geo.latitude)},${Number(signer.geo.longitude)}`}>
          {Number(signer.geo.latitude).toFixed(5)}, {Number(signer.geo.longitude).toFixed(5)}</a>
      : 'Não registrada'],
    ['Aparelho', <span className="break-all">{signer.user_agent ?? '—'}</span>],
  ];
  const kindLabel = { signature_drawing: 'Assinatura desenhada', selfie: 'Selfie' } as const;
  // v1.5.0: imagens das evidências (URLs assinadas de 5 min, buscadas só ao expandir)
  const { data: urls, isError } = useAcceptanceArtifactUrls(instanceId, participantId, true);
  const [zoom, setZoom] = useState<string | null>(null);
  if (isError) log.warn('Falha ao carregar evidências do aceite');
  return (
    <div className="mt-2 space-y-2 rounded-md border bg-muted/30 p-3 text-xs">
      <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1.5">
        {rows.map(([k, v]) => (<Fragment key={k}><dt className="text-muted-foreground">{k}</dt><dd className="min-w-0 font-medium">{v}</dd></Fragment>))}
      </dl>
      {signer.artifacts.length > 0 && (
        <div className="space-y-2 border-t pt-2">
          <div className="text-muted-foreground">Evidências coletadas</div>
          <div className="flex flex-wrap gap-3">
            {signer.artifacts.map((a) => {
              const url = urls?.find((u) => u.sha256 === a.sha256)?.url ?? null;
              return (
                <div key={a.sha256} className="w-40 space-y-1">
                  <div className="font-medium">{kindLabel[a.kind] ?? a.kind}</div>
                  {url ? (
                    <button type="button" onClick={() => setZoom(url)} title="Ampliar"
                      className="flex h-24 w-full items-center justify-center overflow-hidden rounded-md border bg-background hover:ring-2 hover:ring-primary/40">
                      <img src={url} alt={kindLabel[a.kind] ?? a.kind} className="h-full w-full object-contain" />
                    </button>
                  ) : (
                    <div className="flex h-24 w-full items-center justify-center rounded-md border bg-background text-muted-foreground">
                      {urls || isError ? 'Imagem indisponível' : <Loader2 className="h-4 w-4 animate-spin" />}
                    </div>
                  )}
                  <div className="truncate font-mono text-[10px] text-muted-foreground" title={a.sha256}>{a.sha256.slice(0, 16)}…</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <ImageLightbox open={!!zoom} onClose={() => setZoom(null)} imageUrl={zoom ?? undefined} />
      <div className="border-t pt-2 font-mono text-[10px] text-muted-foreground break-all" title="Hash do aceite">
        SHA-256 {signer.assertion_hash}
      </div>
    </div>
  );
}

/** Quantidade de aceites obrigatórios pendentes nas etapas informadas */
export function pendingAcceptances(data: PreparedAcceptance | undefined, stages: AcceptanceStage[]) {
  if (!data?.required) return 0;
  return (data.participants ?? []).filter((p) => p.is_required && p.status !== 'accepted' && stages.includes(p.required_stage)).length;
}

/** Situação do comprovante em PDF do ciclo atual (oculto quando não há aceite concluído) */
export function AcceptanceReceiptBar({ instanceId }: { instanceId: string }) {
  const { data } = useChecklistAcceptanceReceipt(instanceId);
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const label = { pending: 'Comprovante na fila', processing: 'Gerando comprovante', ready: 'Comprovante pronto', failed: 'Falha ao gerar comprovante' }[data.status];
  const tone = data.status === 'ready' ? 'success' : data.status === 'failed' ? 'danger' : 'info';
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card px-3 py-2 text-xs">
      <FileCheck2 className="h-4 w-4 text-primary" />
      <StatusDotLabel tone={tone} label={label} />
      {data.status === 'ready' && data.final_hash && (
        <span className="hidden md:inline text-muted-foreground" title={data.final_hash}>SHA-256 {data.final_hash.slice(0, 12)}…</span>
      )}
      {data.status === 'ready' && (
        <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" disabled={busy}
          onClick={async () => { setBusy(true); await downloadAcceptanceReceipt(instanceId); setBusy(false); }}>
          {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />}Baixar comprovante
        </Button>
      )}
    </div>
  );
}
